import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, AlertTriangle, Trash2, Loader2, CheckCircle2
} from 'lucide-react';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';

const API = `${API_URL}/api`;

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { logout } = useAuth();
  
  const [confirmationWord, setConfirmationWord] = useState('');
  const [expectedWord, setExpectedWord] = useState('ELIMINA');
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deletedSuccess, setDeletedSuccess] = useState(false);

  useEffect(() => {
    loadConfirmationWord();
  }, []);

  const loadConfirmationWord = async () => {
    try {
      const res = await httpClient.get(`${API}/users/me/delete-confirmation`);
      const data = res.data || res;
      setExpectedWord(data?.confirmation_word || 'ELIMINA');
      setConfirmationWord(data?.confirmation_word || 'ELIMINA');
    } catch (err) {
      console.error('Error loading confirmation word:', err);
    }
  };

  const handleDelete = async () => {
    if (userInput.trim().toUpperCase() !== expectedWord) {
      setError(t('deleteAccount.wrongConfirmation') || `Scrivi "${expectedWord}" per confermare`);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await httpClient.post(`${API}/users/me/delete`, { confirmation: userInput.trim() });
      
      // Clear ALL cached data to prevent data leakage after account deletion
      // This is critical - otherwise old data might show if user creates new account
      const keysToRemove = [
        'session_token',
        'feed_cache_all',
        'feed_cache_following', 
        'feed_cache_challenges',
        'feed_ad_banner',
        'dash_cache',
        'transformation_cache',
        'feedFilter',
        'fcm_token',
        'geo_settings_confirmed',
        'motivational_index',
        'motivational_index_date',
        'pending_admin_redirect',
        'walk_calories',
        'walk_distance',
        'walk_pause_start',
        'walk_paused_duration',
        'walk_positions',
        'walk_start_time',
        'walk_status'
      ];
      
      keysToRemove.forEach(key => {
        try { localStorage.removeItem(key); } catch {}
        try { sessionStorage.removeItem(key); } catch {}
      });
      
      // Also clear any other potential cache entries
      try { localStorage.removeItem('app_language'); } catch {}
      
      // Show explicit confirmation screen BEFORE logout so the user sees
      // exactly which data has been deleted. Logout + navigation happens
      // when the user taps the final OK button.
      setDeletedSuccess(true);
    } catch (err) {
      console.error('Error deleting account:', err);
      const errorDetail = err.response?.data?.detail;
      if (errorDetail?.message) {
        setError(errorDetail.message);
      } else {
        setError(t('deleteAccount.error') || 'Errore durante l\'eliminazione');
      }
    } finally {
      setLoading(false);
    }
  };

  // Confirmation screen — shown right after a successful delete. Lists
  // explicitly all data categories that were wiped, then logs the user
  // out + redirects to /login when they tap OK.
  if (deletedSuccess) {
    return (
      <div className={`min-h-full ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex flex-col`}
           style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className={`w-full max-w-md p-6 rounded-3xl text-center ${
            isDark ? 'bg-white/5' : 'bg-white'
          } shadow-xl`}>
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-green-500/15 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-green-500" strokeWidth={2} />
            </div>
            <h2 className={`font-display text-2xl mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('deleteAccount.deletedTitle') || 'Account eliminato'}
            </h2>
            <p className={`text-sm mb-5 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {t('deleteAccount.deletedSubtitle') || 'Tutti i tuoi dati sono stati rimossi in modo permanente:'}
            </p>
            
            <ul className={`text-left text-sm space-y-2 mb-6 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem1') || 'Dati personali (nome, bio, foto profilo)'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem2') || 'Credenziali di accesso (email, password)'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem3') || 'Post, commenti e contenuti pubblicati'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem4') || 'Storico pesate e progressi'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem5') || 'Esercizi, camminate e attività'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('deleteAccount.deletedItem6') || 'Sfide, badge, traguardi e tutti gli altri dati associati'}</span>
              </li>
            </ul>
            
            <button
              onClick={async () => {
                await logout();
                navigate('/login', { replace: true });
              }}
              data-testid="deleted-ok-btn"
              className="w-full py-3.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#E8334A] to-[#C41E3A] shadow-lg active:scale-[0.98] transition-transform"
            >
              {t('deleteAccount.deletedOk') || 'OK'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}
         style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Header - Normal flow, not fixed */}
      <div className={`${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <h1 className={`font-semibold text-red-500`}>
            {t('deleteAccount.title') || 'Elimina Account'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Warning Card */}
        <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-red-500/10' : 'bg-red-50'} border border-red-500/20`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-500 mb-2">
                {t('deleteAccount.warning') || 'Attenzione!'}
              </h3>
              <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {t('deleteAccount.warningText') || 'Questa azione è irreversibile. Tutti i tuoi dati saranno eliminati permanentemente:'}
              </p>
              <ul className={`mt-2 text-sm space-y-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                <li>• {t('deleteAccount.item1') || 'Profilo e impostazioni'}</li>
                <li>• {t('deleteAccount.item2') || 'Tutti i post e commenti'}</li>
                <li>• {t('deleteAccount.item3') || 'Cronologia peso e progressi'}</li>
                <li>• {t('deleteAccount.item4') || 'Piani alimentari e ricette salvate'}</li>
                <li>• {t('deleteAccount.item5') || 'Badge e traguardi'}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Confirmation Section */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
          <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('deleteAccount.confirmTitle') || 'Conferma eliminazione'}
          </h3>
          
          <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {t('deleteAccount.confirmText') || 'Per confermare, scrivi'}{' '}
            <span className="font-bold text-red-500">{confirmationWord}</span>{' '}
            {t('deleteAccount.confirmText2') || 'nel campo sottostante:'}
          </p>

          <input
            type="text"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              setError('');
            }}
            placeholder={confirmationWord}
            className={`w-full px-4 py-3 rounded-xl text-center text-lg font-bold uppercase tracking-wider ${
              isDark 
                ? 'bg-white/10 text-white placeholder-white/30 border border-white/10' 
                : 'bg-gray-100 text-gray-900 placeholder-gray-400 border border-gray-200'
            } ${error ? 'border-red-500' : ''}`}
            data-testid="confirmation-input"
          />
          
          {error && (
            <p className="mt-2 text-sm text-red-500">{error}</p>
          )}

          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={userInput.trim().toUpperCase() !== expectedWord || loading}
            className={`w-full mt-4 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              userInput.trim().toUpperCase() === expectedWord
                ? 'bg-red-500 text-white hover:bg-red-600'
                : isDark 
                  ? 'bg-white/10 text-white/40 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="delete-btn"
          >
            <Trash2 className="w-5 h-5" />
            {t('deleteAccount.deleteButton') || 'Elimina il mio account'}
          </button>
        </div>
      </div>

      {/* Final Confirmation Modal */}
      {showConfirmModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowConfirmModal(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} p-5`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('deleteAccount.finalConfirm') || 'Sei sicuro?'}
                </h3>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('deleteAccount.finalConfirmText') || 'Questa azione non può essere annullata'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className={`flex-1 py-2.5 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              >
                {t('common.cancel') || 'Annulla'}
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl font-medium bg-red-500 text-white flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {t('deleteAccount.confirm') || 'Elimina'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
